import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useEntitySearch, invalidateEntitySearchCache } from "../hooks/useEntitySearch";
import { formatRelationshipStatusLabel } from "../utils/enrichmentJobCard";
import {
  RELATIONSHIP_STATUS_OPTIONS,
  normalizeRelationshipStatus,
  relationshipStatusBadgeClass,
} from "../utils/relationshipStatus";
import {
  getProfileByValue,
  isCatalogCreatableEntityType,
} from "../utils/typeProfile";
import { isTaxonomyHierarchyType } from "../utils/taxonomyHierarchyConfigs";
import { api, invalidateHierarchyNodesCache } from "../services/api";
import HierarchyMultiSelectTree, {
  clearHierarchyTreeSessionCache,
} from "./HierarchyMultiSelectTree";
import RelationshipAttributesDialog, {
  relationshipAttributesFromItem,
} from "./RelationshipAttributesDialog";

const normalizeCreatedEntityResponse = (response) =>
  response?.entity ||
  response?.data ||
  (response?.id ? response : null);

const EntityRelationshipSection = ({
  title,
  icon,
  items = [],
  availableItems = [], // Fallback for backward compatibility
  addLabel = "+ Add",
  onAdd,
  onRemove,
  /** Called when editing attrs on an existing/staged relationship item. */
  onUpdateAttributes = null,
  showAdditionalFields = false,
  readOnly = false,
  maxItems = null,
  typeMetaMap = {},
  loadingAvailable = false,
  /** When true and items are empty, show a loading state instead of "No items". */
  loadingItems = false,
  relatedEntityType = null, // Entity type to search for (enables lazy loading)
  useLazyLoading = true, // Toggle lazy loading (default: true)
  /** Source entity id — enables AI relationship suggestions in the Add dialog. */
  sourceEntityId = null,
  /** "full" = card + list + Add; "toolbar" = Add button + dialog only (parent renders list). */
  variant = "full",
  /** When set, toolbar opens a create dialog only (no catalog search), e.g. solution components. */
  createOnlyEntityType = null,
  createNewLabel = "New component",
  /** Extra fields passed to api.createEntity (e.g. approval_status for solution components). */
  createEntityOptions = {},
  /** When false, hide in-dialog "Create new" even for catalog types. */
  allowCreateInDialog = true,
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [multiSelectStep, setMultiSelectStep] = useState("select");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState(() => new Map());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = useState(false);
  const [browseMode, setBrowseMode] = useState("list"); // list | tree
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState("");
  const [suggestionsHidden, setSuggestionsHidden] = useState(false);
  const [editingAttributesItem, setEditingAttributesItem] = useState(null);
  const [attributesSaving, setAttributesSaving] = useState(false);
  const [additionalFields, setAdditionalFields] = useState({
    activeFrom: "",
    activeUntil: "",
    status: "Proposed",
    description: "",
  });

  // Ensure default status is always a DB-allowed value
  const safeAdditionalStatus = normalizeRelationshipStatus(
    additionalFields.status
  );

  const canEditAttributes =
    !readOnly && typeof onUpdateAttributes === "function";

  // Use lazy loading search hook if enabled and relatedEntityType is provided
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    hasMore: searchHasMore,
    total: searchTotal,
    search: debouncedSearch,
    loadMore: loadMoreResults,
    initialLoad,
    reset: resetSearch,
  } = useEntitySearch(relatedEntityType, {
    enabled: useLazyLoading && !!relatedEntityType && showAddDialog,
    debounceMs: 300,
    pageSize: 50,
  });

  // Initialize search when dialog opens (lazy loading mode)
  useEffect(() => {
    if (showAddDialog && useLazyLoading && relatedEntityType) {
      // Only initial load if search term is empty (first open)
      if (!searchTerm && !hasAttemptedInitialLoad) {
        setHasAttemptedInitialLoad(true);
        initialLoad();
      }
    } else if (!showAddDialog) {
      resetSearch();
      setSearchTerm("");
      setHasAttemptedInitialLoad(false);
      setShowCreateForm(false);
      setCreateName("");
      setCreateDescription("");
      setCreateError("");
      setCreateLoading(false);
      setBrowseMode("list");
      setSuggestions([]);
      setSuggestionsError("");
      setSuggestionsLoading(false);
      setSuggestionsHidden(false);
    }
  }, [
    showAddDialog,
    useLazyLoading,
    relatedEntityType,
    initialLoad,
    resetSearch,
    searchTerm,
    hasAttemptedInitialLoad,
  ]);

  // Trigger search when search term changes (lazy loading mode)
  // Skip if searchTerm is empty (handled by initialLoad)
  useEffect(() => {
    if (showAddDialog && useLazyLoading && relatedEntityType && searchTerm) {
      debouncedSearch(searchTerm, true);
    }
  }, [
    searchTerm,
    showAddDialog,
    useLazyLoading,
    relatedEntityType,
    debouncedSearch,
  ]);

  // Get IDs of items already added to this section
  const addedItemIds = useMemo(
    () => new Set(items.map((item) => item.id).filter(Boolean)),
    [items]
  );

  // Determine which options to show
  const filteredOptions =
    useLazyLoading && relatedEntityType && showAddDialog
      ? searchResults.filter((opt) => !addedItemIds.has(opt.id)) // Use search results, exclude already-added
      : (availableItems || []).filter(
          (opt) =>
            opt?.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !addedItemIds.has(opt.id)
        ); // Fallback to pre-fetched items with client-side filtering, exclude already-added

  const relatedTypeKey = String(relatedEntityType || "").trim().toLowerCase();
  const createOnlyTypeKey = String(createOnlyEntityType || "")
    .trim()
    .toLowerCase();
  const isCreateOnlyMode = Boolean(createOnlyTypeKey);
  const entityTypeKey = createOnlyTypeKey || relatedTypeKey;
  const entityTypeProfile = useMemo(
    () => (entityTypeKey ? getProfileByValue(entityTypeKey) : null),
    [entityTypeKey]
  );
  const entityTypeLabel = useMemo(() => {
    if (entityTypeProfile?.label) return entityTypeProfile.label;
    if (!entityTypeKey) return "entity";
    return entityTypeKey
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [entityTypeKey, entityTypeProfile]);

  const canCreateInDialog =
    allowCreateInDialog &&
    !isCreateOnlyMode &&
    relatedTypeKey &&
    isCatalogCreatableEntityType(relatedTypeKey);

  const supportsTreeBrowse =
    !isCreateOnlyMode && isTaxonomyHierarchyType(relatedTypeKey);

  const isMultiSelectMode = !isCreateOnlyMode && maxItems !== 1;
  const remainingSlots =
    maxItems === null ? Infinity : Math.max(0, maxItems - items.length);
  const selectedItem =
    !isMultiSelectMode && selectedItems.size === 1
      ? Array.from(selectedItems.values())[0]
      : null;
  const selectedCount = selectedItems.size;
  const selectedIdSet = useMemo(
    () => new Set(Array.from(selectedItems.keys())),
    [selectedItems]
  );

  const loadSuggestions = useCallback(async () => {
    if (!sourceEntityId || !relatedTypeKey || isCreateOnlyMode) {
      setSuggestions([]);
      return;
    }
    setSuggestionsLoading(true);
    setSuggestionsError("");
    try {
      const data = await api.getRelationshipSuggestions(
        sourceEntityId,
        relatedTypeKey,
        {
          limit: 3,
          excludeIds: Array.from(addedItemIds),
        }
      );
      const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setSuggestions(list.filter((s) => s?.id && !addedItemIds.has(s.id)));
    } catch (err) {
      setSuggestions([]);
      setSuggestionsError(err?.message || "Could not load suggestions");
    } finally {
      setSuggestionsLoading(false);
    }
  }, [
    sourceEntityId,
    relatedTypeKey,
    isCreateOnlyMode,
    addedItemIds,
  ]);

  useEffect(() => {
    if (
      showAddDialog &&
      !showCreateForm &&
      multiSelectStep === "select" &&
      sourceEntityId &&
      relatedTypeKey &&
      !isCreateOnlyMode &&
      !suggestionsHidden
    ) {
      loadSuggestions();
    }
  }, [
    showAddDialog,
    showCreateForm,
    multiSelectStep,
    sourceEntityId,
    relatedTypeKey,
    isCreateOnlyMode,
    suggestionsHidden,
    loadSuggestions,
  ]);

  const resetDialogFields = useCallback(() => {
    setSearchTerm("");
    setSelectedItems(new Map());
    setMultiSelectStep("select");
    setShowCreateForm(false);
    setCreateName("");
    setCreateDescription("");
    setCreateError("");
    setCreateLoading(false);
    setBrowseMode("list");
    setSuggestions([]);
    setSuggestionsError("");
    setSuggestionsLoading(false);
    setSuggestionsHidden(false);
    setAdditionalFields({
      activeFrom: "",
      activeUntil: "",
      status: "Proposed",
      description: "",
    });
  }, []);

  const handleAddClick = () => {
    resetDialogFields();
    setShowAddDialog(true);
  };

  const handleCreateOnlyClick = () => {
    resetDialogFields();
    setShowCreateForm(true);
    setShowAddDialog(true);
  };

  const handleStartCreate = () => {
    setCreateError("");
    setCreateName(searchTerm.trim());
    setCreateDescription("");
    setShowCreateForm(true);
  };

  const handleCancelCreate = () => {
    if (isCreateOnlyMode) {
      setShowAddDialog(false);
      return;
    }
    setShowCreateForm(false);
    setCreateName("");
    setCreateDescription("");
    setCreateError("");
  };

  const handleSubmitCreate = async () => {
    const trimmedName = String(createName || "").trim();
    const typeKey = createOnlyTypeKey || relatedTypeKey;
    if (!trimmedName || !typeKey) return;
    setCreateLoading(true);
    setCreateError("");
    try {
      const response = await api.createEntity({
        name: trimmedName,
        type: typeKey,
        description: String(createDescription || "").trim() || undefined,
        approval_status: "Proposed",
        processing_status: "Processed",
        ...createEntityOptions,
      });
      const created = normalizeCreatedEntityResponse(response);
      if (!created?.id) {
        throw new Error("Create did not return an entity id");
      }
      const createdItem = {
        ...created,
        type: created.type || created.entity_type || typeKey,
        entity_type: created.entity_type || created.type || typeKey,
      };
      if (isTaxonomyHierarchyType(typeKey)) {
        invalidateHierarchyNodesCache(typeKey);
        clearHierarchyTreeSessionCache(typeKey);
      }
      invalidateEntitySearchCache(typeKey);
      if (isMultiSelectMode) {
        setSelectedItems((prev) => {
          const next = new Map(prev);
          if (next.size < remainingSlots) {
            next.set(createdItem.id, createdItem);
          }
          return next;
        });
      } else {
        setSelectedItems(new Map([[createdItem.id, createdItem]]));
      }
      setShowCreateForm(false);
      setCreateName("");
      setCreateDescription("");
    } catch (err) {
      setCreateError(err?.message || `Failed to create ${entityTypeLabel}.`);
    } finally {
      setCreateLoading(false);
    }
  };

  const createNewButton = canCreateInDialog ? (
    <button
      type="button"
      onClick={handleStartCreate}
      className="text-xs bg-blue-600 text-white px-3 py-1 rounded inline-flex items-center gap-1 hover:bg-blue-700"
    >
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
      Create new {entityTypeLabel}
    </button>
  ) : null;

  const buildItemWithFields = useCallback(
    (item) =>
      showAdditionalFields
        ? {
            ...item,
            activeFrom: additionalFields.activeFrom,
            activeUntil: additionalFields.activeUntil,
            status: normalizeRelationshipStatus(additionalFields.status),
            description: additionalFields.description,
          }
        : item,
    [additionalFields, showAdditionalFields]
  );

  const handleToggleSelection = (opt) => {
    if (isMultiSelectMode) {
      setSelectedItems((prev) => {
        const next = new Map(prev);
        if (next.has(opt.id)) {
          next.delete(opt.id);
        } else if (next.size < remainingSlots) {
          next.set(opt.id, opt);
        }
        return next;
      });
      return;
    }
    setSelectedItems(new Map([[opt.id, opt]]));
  };

  const handleConfirmAdd = () => {
    const itemsToAdd = Array.from(selectedItems.values()).map((item) =>
      buildItemWithFields(item)
    );
    if (itemsToAdd.length === 0) return;
    onAdd(itemsToAdd.length === 1 ? itemsToAdd[0] : itemsToAdd);
    setShowAddDialog(false);
  };

  const handleContinueFromMultiSelect = () => {
    if (selectedCount === 0) return;
    if (showAdditionalFields) {
      setMultiSelectStep("details");
      return;
    }
    handleConfirmAdd();
  };

  const renderAdditionalFields = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-300 mb-1">Active From</label>
          <input
            type="date"
            value={additionalFields.activeFrom}
            onChange={(e) =>
              setAdditionalFields({
                ...additionalFields,
                activeFrom: e.target.value,
              })
            }
            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-300 mb-1">Active Until</label>
          <input
            type="date"
            value={additionalFields.activeUntil}
            onChange={(e) =>
              setAdditionalFields({
                ...additionalFields,
                activeUntil: e.target.value,
              })
            }
            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-300 mb-1">Status</label>
        <select
          value={safeAdditionalStatus}
          onChange={(e) =>
            setAdditionalFields({
              ...additionalFields,
              status: e.target.value,
            })
          }
          className="w-full md:max-w-xs bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
        >
          {RELATIONSHIP_STATUS_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {formatRelationshipStatusLabel(opt) || opt}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-300 mb-1">Description</label>
        <textarea
          value={additionalFields.description}
          onChange={(e) =>
            setAdditionalFields({
              ...additionalFields,
              description: e.target.value,
            })
          }
          rows={3}
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={
            isMultiSelectMode
              ? "Optional notes applied to all selected relationships"
              : "Optional notes about this relationship"
          }
        />
      </div>
    </div>
  );

  const canAddMore = maxItems === null || items.length < maxItems;

  const addButton =
    !readOnly && canAddMore && isCreateOnlyMode ? (
      <button
        type="button"
        onClick={handleCreateOnlyClick}
        className="text-xs bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 hover:bg-blue-700"
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        {createNewLabel}
      </button>
    ) : !readOnly && canAddMore && !isCreateOnlyMode ? (
      <button
        type="button"
        onClick={handleAddClick}
        disabled={loadingAvailable}
        className={`text-xs bg-blue-600 text-white px-3 py-1 rounded flex items-center gap-1 ${
          loadingAvailable
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-blue-700"
        }`}
      >
        {loadingAvailable ? (
          <>
            <svg
              className="animate-spin w-3 h-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading...
          </>
        ) : (
          <>
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {addLabel}
          </>
        )}
      </button>
    ) : null;

  const maxReachedHint =
    !readOnly && maxItems !== null && items.length >= maxItems ? (
      <span className="text-xs text-gray-400 px-3 py-1">
        Maximum reached ({maxItems})
      </span>
    ) : null;

  return (
    <>
      {variant === "toolbar" ? (
        <div className="flex items-center gap-2 shrink-0">
          {addButton}
          {maxReachedHint}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-semibold text-white">
              <span className="mr-2">{icon}</span>
              {title}
            </h5>
            <div className="flex items-center gap-2">
              {addButton}
              {maxReachedHint}
            </div>
          </div>

          <div className="space-y-2 transition-all duration-200 ease-out">
          {loadingItems && items.length === 0 ? (
            <div className="text-xs text-gray-400 flex items-center gap-2">
              <svg
                className="animate-spin w-3 h-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-xs text-gray-400">No items</div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="bg-gray-700 rounded px-3 py-2 transition-transform duration-200 ease-out"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium">
                      {item.name}
                    </span>
                    {(() => {
                      const key = String(
                        item.entity_type || item.type || ""
                      ).toLowerCase();
                      const meta = typeMetaMap[key] || {};
                      const label =
                        meta.label ||
                        key
                          .replace(/-/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase());
                      const color = meta.color || "#4B5563";
                      return (
                        <span
                          className="text-xs text-white px-2 py-1 rounded"
                          style={{ backgroundColor: color }}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canEditAttributes ? (
                      <button
                        type="button"
                        onClick={() => setEditingAttributesItem(item)}
                        className="text-xs text-blue-300 hover:text-blue-200"
                      >
                        Edit details
                      </button>
                    ) : null}
                    {!readOnly && (
                      <button
                        onClick={() => onRemove(item.id)}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {/* Relationship Details */}
                {(item.description ||
                  item.activeFrom ||
                  item.activeUntil ||
                  item.status ||
                  canEditAttributes) && (
                  <div className="mt-2 pt-2 border-t border-gray-600">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      {item.description ? (
                        <div className="col-span-2">
                          <span className="text-gray-400">Description: </span>
                          <span className="text-gray-300">
                            {item.description}
                          </span>
                        </div>
                      ) : null}
                      {item.activeFrom ? (
                        <div>
                          <span className="text-gray-400">Active From: </span>
                          <span className="text-gray-300">
                            {new Date(item.activeFrom).toLocaleDateString()}
                          </span>
                        </div>
                      ) : null}
                      {item.activeUntil ? (
                        <div>
                          <span className="text-gray-400">Active Until: </span>
                          <span className="text-gray-300">
                            {new Date(item.activeUntil).toLocaleDateString()}
                          </span>
                        </div>
                      ) : null}
                      {item.status ? (
                        <div>
                          <span className="text-gray-400">Status: </span>
                          <span
                            className={`px-1 py-0.5 rounded text-xs ${relationshipStatusBadgeClass(
                              item.status
                            )}`}
                          >
                            {formatRelationshipStatusLabel(item.status) ||
                              item.status}
                          </span>
                        </div>
                      ) : canEditAttributes ? (
                        <div className="col-span-2 text-gray-500 italic">
                          No relationship details yet — use Edit details.
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          </div>
        </div>
      )}
      <RelationshipAttributesDialog
        open={Boolean(editingAttributesItem)}
        title="Edit relationship details"
        peerName={editingAttributesItem?.name || ""}
        initialFields={
          editingAttributesItem
            ? relationshipAttributesFromItem(editingAttributesItem)
            : null
        }
        saving={attributesSaving}
        onClose={() => {
          if (attributesSaving) return;
          setEditingAttributesItem(null);
        }}
        onSave={async ({ api: apiPayload, local }) => {
          if (!editingAttributesItem || !onUpdateAttributes) return;
          setAttributesSaving(true);
          try {
            await onUpdateAttributes(editingAttributesItem, {
              api: apiPayload,
              local,
            });
            setEditingAttributesItem(null);
          } catch (err) {
            window.alert(
              err?.message || "Could not update relationship details."
            );
          } finally {
            setAttributesSaving(false);
          }
        }}
      />
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden p-4">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <h4 className="text-white text-sm font-semibold">
                {showCreateForm
                  ? `Create new ${entityTypeLabel}`
                  : isMultiSelectMode && multiSelectStep === "details"
                    ? "Relationship details"
                  : isCreateOnlyMode && selectedCount === 0
                    ? createNewLabel
                    : addLabel}
              </h4>
              <button
                onClick={() => setShowAddDialog(false)}
                className="text-gray-400 hover:text-white text-lg"
              >
                ×
              </button>
            </div>

            {showCreateForm ? (
              <>
                <div className="flex-1 overflow-y-auto min-h-0 space-y-3">
                <p className="text-xs text-gray-400">
                  {isCreateOnlyMode
                    ? `Create a new ${entityTypeLabel} for this application.`
                    : `Create a new ${entityTypeLabel} to link to this entity.`}
                </p>
                {createError ? (
                  <div className="text-red-400 text-sm bg-red-900/30 border border-red-800 rounded px-3 py-2">
                    {createError}
                  </div>
                ) : null}
                <div>
                  <label className="block text-xs text-gray-300 mb-1">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    autoFocus
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`${entityTypeLabel} name`}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    rows={2}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    placeholder="Optional"
                  />
                </div>
                </div>
                <div className="flex justify-between gap-2 pt-3 shrink-0 border-t border-gray-700 mt-3">
                  {!isCreateOnlyMode ? (
                    <button
                      type="button"
                      onClick={handleCancelCreate}
                      disabled={createLoading}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50"
                    >
                      Back to search
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAddDialog(false)}
                      disabled={createLoading}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmitCreate}
                    disabled={!createName.trim() || createLoading}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createLoading ? "Creating…" : "Create"}
                  </button>
                </div>
              </>
            ) : isMultiSelectMode && multiSelectStep === "details" ? (
              <>
                <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-2">
                      Adding {selectedCount}{" "}
                      {selectedCount === 1 ? "relationship" : "relationships"}
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {Array.from(selectedItems.values()).map((item) => (
                        <div
                          key={item.id}
                          className="text-sm text-white bg-gray-700 rounded px-3 py-2"
                        >
                          {item.name}
                        </div>
                      ))}
                    </div>
                  </div>
                  {showAdditionalFields ? renderAdditionalFields() : null}
                </div>
                <div className="flex justify-between gap-2 shrink-0 pt-3 mt-3 border-t border-gray-600">
                  <button
                    type="button"
                    onClick={() => setMultiSelectStep("select")}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                  >
                    Back
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddDialog(false)}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmAdd}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      {`Confirm Add (${selectedCount})`}
                    </button>
                  </div>
                </div>
              </>
            ) : isMultiSelectMode || !selectedItem ? (
              <>
              <div className="flex flex-col flex-1 min-h-0 gap-3">
                <div className="shrink-0 space-y-2">
                  <input
                    type="text"
                    placeholder={`Search ${entityTypeLabel.toLowerCase()}s...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-gray-400">
                      {isMultiSelectMode
                        ? `Select one or more${
                            Number.isFinite(remainingSlots)
                              ? ` (up to ${remainingSlots} more)`
                              : ""
                          }`
                        : "Select an item"}
                      {selectedCount > 0 ? ` · ${selectedCount} selected` : ""}
                    </p>
                    {supportsTreeBrowse ? (
                      <div
                        className="inline-flex rounded-md border border-gray-600 overflow-hidden text-xs"
                        role="tablist"
                        aria-label="Browse mode"
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={browseMode === "list"}
                          onClick={() => setBrowseMode("list")}
                          className={`px-2.5 py-1 ${
                            browseMode === "list"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-650"
                          }`}
                        >
                          List
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={browseMode === "tree"}
                          onClick={() => setBrowseMode("tree")}
                          className={`px-2.5 py-1 border-l border-gray-600 ${
                            browseMode === "tree"
                              ? "bg-blue-600 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-650"
                          }`}
                        >
                          Tree
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto space-y-3">
                  {/* AI suggestions — all entity types */}
                  {sourceEntityId && relatedTypeKey && !isCreateOnlyMode ? (
                    suggestionsHidden ? (
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-600 bg-gray-800/60 px-3 py-2">
                        <span className="text-xs text-gray-300">
                          Suggestions hidden
                        </span>
                        <button
                          type="button"
                          onClick={() => setSuggestionsHidden(false)}
                          className="text-xs font-medium text-blue-300 hover:text-blue-200"
                        >
                          Show suggestions
                        </button>
                      </div>
                    ) : (
                    <div className="rounded-lg border border-blue-700 bg-blue-900/30 p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <h5 className="text-sm font-semibold text-white">
                            Suggested for this item
                          </h5>
                          <p className="text-xs text-gray-300 mt-0.5">
                            Based on this item’s description and related map
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            type="button"
                            onClick={loadSuggestions}
                            disabled={suggestionsLoading}
                            className="text-xs font-medium text-blue-300 hover:text-blue-200 disabled:opacity-50"
                          >
                            {suggestionsLoading ? "Loading…" : "Refresh"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSuggestionsHidden(true)}
                            className="text-xs font-medium text-gray-300 hover:text-white"
                            aria-label="Hide suggestions"
                          >
                            Hide
                          </button>
                        </div>
                      </div>
                      {suggestionsLoading && suggestions.length === 0 ? (
                        <p className="text-sm text-gray-300">Finding matches…</p>
                      ) : suggestionsError && suggestions.length === 0 ? (
                        <p className="text-sm text-gray-300">{suggestionsError}</p>
                      ) : suggestions.length === 0 ? (
                        <p className="text-sm text-gray-300">
                          No strong catalog matches yet — use search or browse below.
                        </p>
                      ) : (
                        <div className="space-y-1.5">
                          {suggestions.map((sug) => {
                            const isSelected = selectedIdSet.has(sug.id);
                            return (
                              <button
                                key={sug.id}
                                type="button"
                                onClick={() => handleToggleSelection(sug)}
                                className={`w-full text-left rounded-md border px-2.5 py-2 flex items-start gap-2 transition-colors ${
                                  isSelected
                                    ? "bg-blue-900/40 border-blue-500"
                                    : "bg-gray-800 border-gray-600 hover:border-blue-500"
                                }`}
                              >
                                {isMultiSelectMode ? (
                                  <input
                                    type="checkbox"
                                    readOnly
                                    checked={isSelected}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-500 text-blue-600 pointer-events-none"
                                    aria-hidden
                                  />
                                ) : null}
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-white font-semibold">
                                    {sug.name}
                                  </div>
                                  {sug.reason ? (
                                    <div className="text-sm text-gray-200 mt-0.5 leading-snug">
                                      {sug.reason}
                                    </div>
                                  ) : null}
                                  {sug.description ? (
                                    <div className="text-xs text-gray-300 mt-1 line-clamp-2 leading-snug">
                                      {sug.description}
                                    </div>
                                  ) : null}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    )
                  ) : null}

                  {browseMode === "tree" && supportsTreeBrowse ? (
                    <HierarchyMultiSelectTree
                      entityType={relatedTypeKey}
                      selectedIds={selectedIdSet}
                      onToggle={handleToggleSelection}
                      excludeIds={addedItemIds}
                      searchQuery={searchTerm}
                    />
                  ) : loadingAvailable ||
                    searchLoading ||
                    (useLazyLoading &&
                      relatedEntityType &&
                      !hasAttemptedInitialLoad) ? (
                    <div className="text-gray-400 text-sm text-center py-6">
                      <div className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin w-4 h-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Loading items...</span>
                      </div>
                    </div>
                  ) : searchError ? (
                    <div className="text-red-400 text-sm text-center py-6">
                      Error: {searchError}
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div className="text-gray-400 text-sm text-center py-6 space-y-3">
                      <p>
                        {useLazyLoading && relatedEntityType
                          ? searchTerm
                            ? "No entities found matching your search"
                            : "No entities in the catalog yet."
                          : "No items found"}
                      </p>
                      {createNewButton}
                    </div>
                  ) : (
                    <>
                      {useLazyLoading && relatedEntityType && searchTotal > 0 && (
                        <div className="text-xs text-gray-400 mb-1 px-1">
                          Found {searchTotal}{" "}
                          {searchTotal === 1 ? "entity" : "entities"}
                          {searchTerm && ` matching "${searchTerm}"`}
                        </div>
                      )}
                      {filteredOptions.map((opt) => {
                        const isSelected = selectedIdSet.has(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => handleToggleSelection(opt)}
                            className={`w-full text-left p-3 rounded border transition-colors ${
                              isSelected
                                ? "bg-blue-900/40 border-blue-500 hover:bg-blue-900/50"
                                : "bg-gray-700 hover:bg-gray-600 border-gray-600 hover:border-gray-500"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {isMultiSelectMode ? (
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={isSelected}
                                  className="mt-0.5 h-4 w-4 rounded border-gray-500 text-blue-600 pointer-events-none"
                                  aria-hidden
                                />
                              ) : null}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-white text-sm font-medium">
                                    {opt.name}
                                  </div>
                                  {(() => {
                                    const key = String(
                                      opt.entity_type || opt.type || ""
                                    ).toLowerCase();
                                    const meta = typeMetaMap[key] || {};
                                    const label =
                                      meta.label ||
                                      key
                                        .replace(/-/g, " ")
                                        .replace(/\b\w/g, (c) =>
                                          c.toUpperCase()
                                        );
                                    const color = meta.color || "#4B5563";
                                    return (
                                      <span
                                        className="text-xs text-white px-2 py-1 rounded shrink-0"
                                        style={{ backgroundColor: color }}
                                      >
                                        {opt.subtype
                                          ? `${label} (${opt.subtype})`
                                          : label}
                                      </span>
                                    );
                                  })()}
                                </div>
                                {opt.description && (
                                  <div className="text-gray-400 text-xs mt-1 line-clamp-2">
                                    {opt.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {useLazyLoading && relatedEntityType && searchHasMore && (
                        <button
                          onClick={loadMoreResults}
                          disabled={searchLoading}
                          className="w-full text-center p-2 bg-gray-700 hover:bg-gray-600 rounded border border-gray-600 hover:border-gray-500 text-gray-300 text-sm disabled:opacity-50"
                        >
                          {searchLoading ? "Loading..." : "Load More"}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Selection tray */}
                {selectedCount > 0 ? (
                  <div className="shrink-0 border-t border-gray-600 pt-2 space-y-2">
                    <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                      {Array.from(selectedItems.values()).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleToggleSelection(item)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-900/50 border border-blue-600/60 text-xs text-blue-100 hover:bg-blue-900/70"
                          title="Remove from selection"
                        >
                          <span className="truncate max-w-[10rem]">{item.name}</span>
                          <span aria-hidden>×</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {canCreateInDialog &&
                (browseMode === "list" ? filteredOptions.length > 0 : true) ? (
                  <div className="pt-2 border-t border-gray-600 flex justify-center shrink-0">
                    {createNewButton}
                  </div>
                ) : null}
              </div>
                <div className="shrink-0 pt-3 mt-1 border-t border-gray-600 flex justify-between items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedItems(new Map())}
                    disabled={selectedCount === 0}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 disabled:opacity-40"
                  >
                    Clear selection
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddDialog(false)}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    {isMultiSelectMode ? (
                      <button
                        type="button"
                        onClick={handleContinueFromMultiSelect}
                        disabled={selectedCount === 0}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                      >
                        {showAdditionalFields
                          ? `Continue (${selectedCount})`
                          : `Confirm Add (${selectedCount})`}
                      </button>
                    ) : selectedItem ? (
                      <button
                        type="button"
                        onClick={handleConfirmAdd}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                      >
                        Continue
                      </button>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto min-h-0 space-y-4">
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-white text-sm font-medium">
                    {selectedItem.name}
                  </div>
                  {(() => {
                    const key = String(
                      selectedItem.entity_type || selectedItem.type || ""
                    ).toLowerCase();
                    const meta = typeMetaMap[key] || {};
                    const label =
                      meta.label ||
                      key
                        .replace(/-/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase());
                    const color = meta.color || "#4B5563";
                    return (
                      <span
                        className="text-xs text-white px-2 py-1 rounded"
                        style={{ backgroundColor: color }}
                      >
                        {selectedItem.subtype
                          ? `${label} (${selectedItem.subtype})`
                          : label}
                      </span>
                    );
                  })()}
                </div>

                {showAdditionalFields ? renderAdditionalFields() : null}
                </div>

                <div className="flex justify-between gap-2 shrink-0 pt-3 mt-3 border-t border-gray-600">
                  <button
                    type="button"
                    onClick={() => setSelectedItems(new Map())}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                  >
                    Change selection
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddDialog(false)}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmAdd}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      Confirm Add
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EntityRelationshipSection;
